import React, { FC, JSX, useCallback, useEffect, useMemo, useState } from 'react';

import {
	BUTTON_TYPES,
	EvConfirm,
	EvLink,
	EvLinkTypes,
	EvLoader,
	EvSvgIcon,
	EvTableColumn,
	SORT_ORDER,
	X
} from '@evinced-private/ui-common';

import { SITE_SCANNER_APP_ID } from '../../../../consts/dom-consts';
import { removeAllTrailingSlashes } from '../../../../helpers/UrlFormattingHelper';
import { IUrlSeedDisplay } from '../../../../interfaces/UrlSeedDisplay';
import { useTablesStore } from '../../../../providers/tablesProvider/TablesProvider';
import { SiteScannerTable } from '../../../common/site-scanner-table/SiteScannerTable';
import { SearchFilter } from '../../../search-filter/SearchFilter';

import { getValidSelectedIds } from './seedsTableHelpers';
import { SeedsTableSelectionActions } from './SeedsTableSelectionActions';

import './SeedsTable.scss';

interface ISeedsListProps {
	seeds: IUrlSeedDisplay[];
	selectedSeeds: IUrlSeedDisplay[];
	setSelectedSeeds: (seeds: IUrlSeedDisplay[]) => void;
	updatePropertySeeds: (seeds: IUrlSeedDisplay[], closeModal: () => void) => void;
	propertyId: string;
	isListMode: boolean;
	isImporting?: boolean;
}

export const SeedsTable: FC<ISeedsListProps> = ({
	seeds,
	selectedSeeds,
	setSelectedSeeds,
	updatePropertySeeds,
	propertyId,
	isListMode,
	isImporting = false
}: ISeedsListProps) => {
	const [filteredSeeds, setFilteredSeeds] = useState(seeds);
	const [searchFilterValue, setSearchFilterValue] = useState('');
	const tablesStore = useTablesStore();

	// Scope the persisted pagination per mode. List and crawl can hold very
	// different row counts, and a stale page from one mode would otherwise
	// follow the user into the other (e.g. page 3 in CRAWL with 50 seeds
	// carries into LIST with 5 urls — EvTable then slices past the end and
	// crashes the SelectionProvider on `d.id`).
	const tableId = `seeds-table-${propertyId}-${isListMode ? 'list' : 'crawl'}`;

	useEffect(() => {
		const filtered = seeds.filter((seed) => {
			return seed.url.toLowerCase().includes(searchFilterValue.toLowerCase());
		});
		setFilteredSeeds(filtered);
	}, [searchFilterValue, seeds]);

	// Drops stale selected IDs that no longer exist in `seeds` (e.g. after
	// switching list/crawl modes). See `getValidSelectedIds` for the why.
	const validSelectedIds = useMemo(
		() => getValidSelectedIds(selectedSeeds, seeds),
		[selectedSeeds, seeds]
	);

	const onSelectAll = (isSelect, seeds): void => {
		if (isSelect) {
			setSelectedSeeds(seeds);
		} else {
			setSelectedSeeds([]);
		}
	};

	const onInputChange = (newValue: string): void => {
		const { sizePerPage } = tablesStore.getPagination(tableId);

		tablesStore.updatePagination(tableId, { page: 1, sizePerPage });
		setSearchFilterValue(newValue);
	};

	const onSelectRow = (row, isSelect): void => {
		let newSelectedSeeds = [...selectedSeeds];
		if (isSelect) {
			newSelectedSeeds.push(row);
		} else {
			newSelectedSeeds = newSelectedSeeds.filter((seed) => seed.url !== row.url);
		}
		setSelectedSeeds(newSelectedSeeds);
	};

	const selectableTableActionsCellFormatter = (cell, row): JSX.Element => {
		return (
			<div className="actions-cell">
				<EvConfirm
					appElement={SITE_SCANNER_APP_ID}
					title="Confirm removing URL"
					promptMessage={`Are you sure you want to remove "${row.url}"?`}
					triggerButtonProps={{
						type: BUTTON_TYPES.ICON,
						title: 'Remove URL',
						children: <EvSvgIcon icon={X} isSmall />
					}}
					onConfirm={(closeModal) => {
						let newSeeds = [...seeds];
						newSeeds = newSeeds.filter((s) => s.url !== row.url);
						updatePropertySeeds(newSeeds, closeModal);
					}}
				/>
			</div>
		);
	};

	const getColumns = (): EvTableColumn[] => {
		return [
			{
				dataField: 'url',
				text: 'URL',
				classes: 'clickable-cell',
				sort: true,
				// eslint-disable-next-line react/no-unstable-nested-components
				formatter: (url: string) => {
					const formattedUrl = removeAllTrailingSlashes(url);
					return (
						<EvLink
							style={{ maxWidth: 'unset' }}
							url={url}
							linkText={formattedUrl}
							customTooltip={{ dataTip: url, dataFor: 'table-tooltip' }}
							type={EvLinkTypes.DARK}
						/>
					);
				}
			},
			{
				dataField: 'actions',
				text: '',
				classes: 'actions-cell-td',
				style: { width: '30px' },
				headerStyle: { width: '30px' },
				formatter: selectableTableActionsCellFormatter
			}
		];
	};

	const renderIsImporting = useCallback(
		(): JSX.Element => (
			<div className="loader">
				<EvLoader message="Importing data..." />
			</div>
		),
		[]
	);
	const topRowActionsProps = isImporting
		? { topRowActions: renderIsImporting(), alignActionsRight: false }
		: {};

	return (
		<div className="property-urls-table">
			<div className="property-urls-table-toolbar">
				<SearchFilter
					searchInputValue={searchFilterValue}
					onInputChange={onInputChange}
					withBottomMargin={false}
				/>
				<SeedsTableSelectionActions
					seeds={seeds}
					selectedSeeds={selectedSeeds}
					updatePropertySeeds={updatePropertySeeds}
				/>
			</div>
			<SiteScannerTable
				tableId={tableId}
				tableData={filteredSeeds}
				totalCount={seeds.length}
				noDataIndicationMessage="No Seeds..."
				columns={getColumns()}
				persistTableState
				options={{
					title: 'Property Seeds',
					pagination: true,
					caption: 'URLs table',
					dataType: 'urls',
					defaultSorted: [
						{
							dataField: 'url',
							order: SORT_ORDER.ASC
						}
					],
					selectRow: {
						onSelect: onSelectRow,
						onSelectAll,
						selected: validSelectedIds
					}
				}}
				{...topRowActionsProps}
			/>
		</div>
	);
};
